import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router";
import i18n from "src/i18n/config";
import { formatNumber } from "src/i18n/formatNumber";
import { PermissionService } from "src/services/permissionService/permissionService";
import { useDispatch } from "src/store";

import { showDetails } from "@devices/store";
import { Group } from "@devices/types";
import UserSettingsService from "@services/userSettingsService";
import { DangerOutlinedButton } from "@shared/CustomButton";
import { HoverRow } from "@shared/table/HoverRow";
import { MinimalTableCell } from "@shared/table/MinimalTableComponents";
import { TextLink } from "@shared/TextLink";
import { useCurrentWorkspace } from "@workspaces/api/helpers";

import { ComplianceCell } from "./ComplianceCell";

type Props = {
    group: Group;
    /**
     * The hash of the policy the page shows, which the group is assigned to.
     */
    policyHash: string | null;
    isUnassigning: boolean;
    disabled: boolean;
    onUnassign: (groupId: number) => void;
};

export const AssignedGroupRow = ({ group, policyHash, isUnassigning, disabled, onUnassign }: Props) => {
    const { t } = useTranslation(["pages", "common"]);
    const dispatch = useDispatch();
    const navigate = useNavigate();

    const { groupAbility } = PermissionService();
    const { data: workspace = null } = useCurrentWorkspace();

    const cannotUnassignPolicy = groupAbility(group, workspace).cannot("unassign", "Policy");

    // A saved filter or search on the groups page could hide the row of the group being opened.
    const showGroup = () => {
        UserSettingsService.setWorkspaceValue("groupPage.columnFilters", []);
        UserSettingsService.setWorkspaceValue("groupPage.search", "");
        dispatch(showDetails({ type: "group", selectedId: group.id }));
        void navigate("/dashboard/groups");
    };

    return (
        <HoverRow clickable={false}>
            <MinimalTableCell align="left">
                <TextLink onClick={showGroup} tooltip={t("common:tooltip.showGroupDetails")}>
                    {group.name}
                </TextLink>
            </MinimalTableCell>
            <MinimalTableCell align="left">
                {formatNumber(group.statistics?.own.deviceCount ?? 0, i18n.language)}
            </MinimalTableCell>
            <MinimalTableCell align="left">
                <ComplianceCell group={group} policyHash={policyHash} />
            </MinimalTableCell>
            <MinimalTableCell align="right">
                <DangerOutlinedButton
                    onClick={() => onUnassign(group.id)}
                    loading={isUnassigning}
                    disabled={disabled || cannotUnassignPolicy}
                    tooltipProps={{
                        title: cannotUnassignPolicy
                            ? t("common:tooltip.cannotManagePolicies")
                            : t("pages:policiesPage.tooltip.unassignGroup"),
                    }}
                >
                    <i className="fa-solid fa-fw fa-trash" />
                </DangerOutlinedButton>
            </MinimalTableCell>
        </HoverRow>
    );
};
